import {Link} from 'react-router-dom';
import {memoize} from 'lodash';

import {Button, Icon} from '@deviceinsight/ng-ui-basic-components';
import {ReactableColumnDef} from '@deviceinsight/ng-ui-components';
import {DataProcessingDefinition, AssetGroupAssignment} from '../../api/dataProcessingDefinitionApi';

import {Trans, hasAuthorities} from '@deviceinsight/ng-ui-scale-lib/api';
import {DATAPOINT_TYPE_LABELS, DpTypeIcon} from './datapoints/DpTypeIcon';
import {AssignedAssetGroupsPerDPD, MissingDatapointsPerDPD} from './types';
import TooltipBubble from './TooltipBubble';

import ShareButton from './reactable/ShareButton';
import {
	AUTHORITY_ASSIGN_KPI_DATA_PROCESSING_DEFINITION_TO_ASSET,
	AUTHORITY_ASSIGN_KPI_DATA_PROCESSING_DEFINITION_TO_ASSET_GROUP
} from '../common/consts';

function hasAuthoritiesAssetAssignUnassign() {
	return hasAuthorities(['UPDATE_ASSET', AUTHORITY_ASSIGN_KPI_DATA_PROCESSING_DEFINITION_TO_ASSET]);
}

function hasAuthoritiesAssetGroupShareUnshare() {
	return hasAuthorities(['UPDATE_ASSET_GROUP', AUTHORITY_ASSIGN_KPI_DATA_PROCESSING_DEFINITION_TO_ASSET_GROUP]);
}

export const getStaticColumnDefs = memoize(
	(missingDatapointsPerDPD: MissingDatapointsPerDPD = {}): Array<ReactableColumnDef<DataProcessingDefinition>> => {
		return [
			{
				name: 'kpiName',
				label: <Trans i18nKey="asset.detail.dataProcessingDefinition.columns.kpiName">Definition Name</Trans>,
				width: '18%',
				sortable: true,
				filterable: {
					filterType: 'text',
					filterFn: (item: DataProcessingDefinition, value: string) => {
						return item.definition.kpiName.toLowerCase().indexOf(value.toLowerCase()) >= 0;
					}
				},
				render: (item: DataProcessingDefinition) =>
					item.id ? (
						// @ts-ignore
						<Link to={`dataprocessingdefinitions/edit/${item.id}`} state={item}>
							{item.definition.kpiName}
						</Link>
					) : (
						'-'
					)
			},
			{
				name: 'processingPeriod',
				label: (
					<Trans i18nKey="asset.detail.dataProcessingDefinition.columns.processingPeriod">
						Processing period
					</Trans>
				),
				width: '18%',
				sortable: true,
				filterable: {
					filterType: 'text',
					filterFn: (item: DataProcessingDefinition, value: string) => {
						return item.definition.processingPeriod.toLowerCase().indexOf(value.toLowerCase()) >= 0;
					}
				},
				render: (item: DataProcessingDefinition) => (item.id ? item.definition.processingPeriod : '-')
			},
			{
				name: 'publishAsDatapointValues',
				label: (
					<Trans i18nKey="asset.detail.dataProcessingDefinition.columns.publishAsDatapointValues">Type</Trans>
				),
				width: '18%',
				render: ({definition: {publishAsDatapointValues}}: DataProcessingDefinition) => {
					const publishedType = publishAsDatapointValues
						? DATAPOINT_TYPE_LABELS[0]
						: DATAPOINT_TYPE_LABELS[1];
					return (
						<span>
							<DpTypeIcon dpType={publishedType.value} />
							{typeof publishedType.label === 'string' ? (
								publishedType.label
							) : (
								<Trans i18nKey={publishedType.label.id}>{publishedType.label.defaultValue}</Trans>
							)}
						</span>
					);
				}
			},
			{
				name: 'inputDatapoints',
				label: (
					<Trans i18nKey="asset.detail.dataProcessingDefinition.columns.inputDatapoints">
						Input variables
					</Trans>
				),
				width: '18%',
				render: (item: DataProcessingDefinition) => {
					const {
						id,
						definition: {input}
					} = item;
					const datapointNames = input.map((dp) => dp.datapointKey);
					const missingDatapointNames: string[] = missingDatapointsPerDPD[id]
						? (missingDatapointsPerDPD[id] as string[])
						: [];
					const availableDatapointNames = datapointNames.filter((dp) => !missingDatapointNames.includes(dp));
					return (
						<TooltipBubble
							availableDatapoints={availableDatapointNames}
							missingDatapoints={missingDatapointNames}
						/>
					);
				}
			}
		];
	}
);
export const getSharedWithAssetGroupsColumnDef = (
	sharedDataProcessingDefinitions: AssignedAssetGroupsPerDPD,
	onUnshare: (dpd: DataProcessingDefinition, assetGroupAssignment: AssetGroupAssignment) => any
): ReactableColumnDef<DataProcessingDefinition> => ({
	name: 'sharing',
	label: (
		<Trans i18nKey="asset.detail.dataProcessingDefinition.columns.sharing">
			Sharing of Data Processing Definitions
		</Trans>
	),
	width: '15%',
	sortable: false,
	render: (item: DataProcessingDefinition) => (
		<div
			className="share-container"
			style={{
				textAlign: 'left'
			}}>
			<span className="di closeable-tag close-inside">
				{sharedDataProcessingDefinitions[item.id] &&
					sharedDataProcessingDefinitions[item.id].map((assetGroupAssignment, i) => (
						<span key={i} className="di label">
							<span
								style={{
									paddingRight: '4px'
								}}>
								<Link
									to={`/assetgroups/${assetGroupAssignment.assetGroupId}/dataprocessingdefinitions`}
									replace>
									<Trans i18nKey="assets.detail.conditionMonitoring.sharedLabel">
										{assetGroupAssignment.assetGroupDisplayName ||
											assetGroupAssignment.assetGroupName}
									</Trans>
								</Link>
							</span>
							{hasAuthoritiesAssetGroupShareUnshare() && (
								<Button
									naked
									small
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: 20,
										height: 20,
										borderRadius: 999,
										textDecoration: 'none',
										marginTop: 2,
										marginRight: -3
									}}
									onClick={() => onUnshare(item, assetGroupAssignment)}>
									⨉
								</Button>
							)}
						</span>
					))}
			</span>
		</div>
	)
});

type UnassignButtonProps = {
	item: DataProcessingDefinition;
	onUnassign: (dataProcessingDefinition: DataProcessingDefinition) => void;
};

const UnassignButton = ({item, onUnassign}: UnassignButtonProps) => (
	<Button
		naked
		disabled={!item.directlyAssigned}
		style={{
			pointerEvents: 'auto',
			background: item.directlyAssigned ? undefined : 'transparent'
		}}
		title={
			item.directlyAssigned
				? {
						id: 'assets.detail.dataProcessingDefinition.unassignButtonLabel',
						defaultValue: 'Unassign from Asset'
				  }
				: {
						id: 'assets.detail.dataProcessingDefinition.disabledUnassignButtonLabel',
						defaultValue: 'Unshare it first'
				  }
		}
		onClick={() => onUnassign(item)}
		data-testid="unassign-data-processing-definition-button">
		<Icon del />
	</Button>
);

export const getUtilitiesButtonColumnDef = (
	onUnassign: (dataProcessingDefinition: DataProcessingDefinition) => void,
	onShare: (arg0: DataProcessingDefinition) => any
): ReactableColumnDef<DataProcessingDefinition> => ({
	name: 'utilityButtons',
	label: '',
	width: '6%',
	sortable: false,
	align: 'right',
	render: (item: DataProcessingDefinition) => (
		<div
			className="buttonsContainer"
			style={{
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'flex-end'
			}}>
			{hasAuthoritiesAssetGroupShareUnshare() && <ShareButton onClick={() => onShare(item)} />}
			{hasAuthoritiesAssetAssignUnassign() && item.id ? (
				<UnassignButton onUnassign={onUnassign} item={item} />
			) : (
				''
			)}
		</div>
	)
});
